import {
    forwardRef,
    memo,
    PropsWithChildren,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react'
import {
    IMultiPaneLayout,
    MultiPaneLayoutContextType,
    MultiPaneLayoutProps,
    MultiPanesConfig,
    MultiPaneStyles
} from '../multiPaneLayout.interface'
import PaneContainer from '../paneContainer/paneContainer'
import { MultiPaneLayoutContext } from '../multipPaneLayout.context'
import {
    findParentPane,
    generateLayoutConfig,
    generatePaneLayoutsMap,
    getMultiPaneLayoutStyles,
    updatePanesOnDelete,
    updatePanesOnResize,
    updatePanesOnSplit,
    updatePanesToEqualSize
} from '../multipPaneLayout.helper'
import useStyles from './styles'

const MultiPaneLayout = forwardRef<any, PropsWithChildren<MultiPaneLayoutProps>>((
    { layout, onLayoutChange, maxPanes, children, hidePaneActionButtons, onPaneAdded, onPaneDeleted, onMouseLeave }, ref) => {

    const { classes } = useStyles()
    const [container, setContainer] = useState<HTMLDivElement>()
    const previousStyles = useRef<MultiPaneStyles>({})
    const [layoutContext, setLayoutContext] = useState<MultiPaneLayoutContextType>()
    const resizeObserver = useRef<ResizeObserver>(null);
    const currentLayoutContext = useRef<MultiPaneLayoutContextType>(null);

    useImperativeHandle<any, IMultiPaneLayout>(ref, () => {
        return {
            splitPane(paneId, numberOfDivisions, horizontal) {
                if (!layoutContext) return
                const parentNode = findParentPane(paneId, layoutContext.panesConfig)
                layoutContext.splitPane(paneId, numberOfDivisions, horizontal, parentNode ? parentNode.id : undefined)
            },
            deletePane(paneId) {
                if (!layoutContext) return
                const visiblePanes = Object.values(layoutContext.panesConfig).filter((p) => p.panes.length === 0)
                if (visiblePanes.length <= 1) return
                const parentNode = findParentPane(paneId, layoutContext.panesConfig)
                if (parentNode) {
                    layoutContext.deletePane(paneId, parentNode.id)
                }
            }
        }
    }, [layoutContext])


    const updateLayoutContext = useCallback((panesConfig: MultiPanesConfig, notify = true, structural = false) => {
        const panes = Object.values(panesConfig)
        if (!container || !panes.length) return
        let root = panes[0]
        while (true) {
            const parent = findParentPane(root.id, panesConfig)
            if (!parent) break
            root = parent
        }
        const paneStyles = getMultiPaneLayoutStyles(root.id, container!.clientWidth, container!.clientHeight, panesConfig)

        const onPaneResizeStart = () => {
            previousStyles.current = paneStyles
        }

        const onPaneResizeStop = () => {
            previousStyles.current = {}
        }

        const onPaneResize = (paneId: number, dividerIndex: number, deltaX: number, deltaY: number) => {
            const newPanesConfig = updatePanesOnResize(paneId, dividerIndex, deltaX, deltaY, panesConfig, previousStyles.current)
            updateLayoutContext(newPanesConfig)
        }

        const onPaneEqualize = (paneId: number) => {
            const newPanesConfig = updatePanesToEqualSize(paneId, panesConfig)
            updateLayoutContext(newPanesConfig)
        }

        const splitPane = (paneId: number, numberOfDivisions: number, horizontal: boolean, parentPaneId?: number) => {
            const newPanesConfig = updatePanesOnSplit(paneId, numberOfDivisions, horizontal, panesConfig, parentPaneId)
            
            const ids = Object.keys(newPanesConfig).map(s => parseInt(s))

            const visiblePanes = Object.values(newPanesConfig).filter(p => p.panes.length === 0)
            if (maxPanes && visiblePanes.length > maxPanes) {
                return // maximum limit reached, do not allow more panes
            }
            const newPaneId = Math.max(...ids)
            // Widget add/remove and the pane-tree update are reported in the same tick so the
            // dashboard never observes (and never persists) a tree with a widget-less pane or
            // a widget without a pane.
            if (onPaneAdded(newPaneId, panesConfig, newPanesConfig, paneId) === false) return
            updateLayoutContext(newPanesConfig, true, true)
        }

        const deletePane = (paneId: number, parentPaneId: number) => {
            const newPanesConfig = updatePanesOnDelete(paneId, parentPaneId, panesConfig)
            onPaneDeleted(paneId, panesConfig, newPanesConfig)
            updateLayoutContext(newPanesConfig, true, true)
        }

        const context: MultiPaneLayoutContextType = {
            rootId: root.id,
            panesConfig,
            paneStyles,
            onPaneResize,
            onPaneResizeStart,
            onPaneResizeStop,
            onPaneEqualize,
            splitPane,
            deletePane
        };

        currentLayoutContext.current = context;

        setLayoutContext(context)
        notify && onLayoutChange(generateLayoutConfig(panesConfig, { ...root }), structural)
    }, [container, layout])

    useEffect(() => {
        if (!container || !layout) return
        updateLayoutContext(generatePaneLayoutsMap(layout), false);
    }, [container, layout])

    useEffect(() => {
        if (!container || !currentLayoutContext.current) return
        resizeObserver.current = new ResizeObserver((entries: ResizeObserverEntry[]) => {
            if (!Array.isArray(entries) || !entries.length) {
                return
            }
            const paneStyles = getMultiPaneLayoutStyles(currentLayoutContext.current.rootId, container!.clientWidth, container!.clientHeight, currentLayoutContext.current.panesConfig)
            setLayoutContext({
                ...currentLayoutContext.current,
                paneStyles
            })
        })
        resizeObserver.current.observe(container)

        return () => {
            resizeObserver.current?.disconnect();
        }
    }, [container])

    return (

        <div ref={(ele) => {
            ele && setContainer(ele)
        }} className={classes.container} onMouseLeave={onMouseLeave}>
            {layoutContext && (
                <MultiPaneLayoutContext.Provider value={layoutContext}>
                    <PaneContainer paneId={layoutContext.rootId} key={layoutContext.rootId}
                                   hidePaneActionButtons={hidePaneActionButtons} />
                    {children}
                </MultiPaneLayoutContext.Provider>
            )}
        </div>
    )
})

MultiPaneLayout.displayName = 'MultiPaneLayout'


export default memo(MultiPaneLayout)